/**
 * U cut hairstyles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_named_haircuts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const uCutHairstylesPack: ToilePack = {
    slug: 'u-cut-hairstyles',
    category: 'beauty',
    name: 'U cut hairstyles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'long', label: 'Long U', subject: 'long straight hair cut in a soft U shape, seen from behind'},
        {key: 'layered', label: 'Layered U', subject: 'a U cut with layers through the length, seen from behind'},
        {key: 'medium', label: 'Medium U', subject: 'shoulder-blade hair in a U cut with a slight wave, seen from behind'},
    ],
    styles: [
        {key: 'salon', label: 'Salon', style: 'photographed in a bright hair salon by soft broad window daylight from camera-left, a light and open balance with few dark tones', palette: 'white walls and pale wood, the hair the darkest tone', framing: 'framed from the crown to below the hem of the hair at eye level'},
        {key: 'sunlit', label: 'Sunlit', style: 'outdoors on a terrace in warm late sun from camera-right raking across the hair, a medium balance with soft shadow in the layers', palette: 'terracotta and warm stone', framing: 'framed from the crown to below the hem, shallow depth of field'},
        {key: 'home', label: 'At home', style: 'indoors by a tall window at home, soft daylight from overhead and a little to the left, a light balance with gentle shadow at the nape', palette: 'linen white and pale oak', framing: 'framed from the crown to below the hem, the room soft behind'},
    ],
    directions: ['caramel balayage', 'for thick hair', 'wedding look'],
    directionHint: 'try: caramel balayage',
    seo: {
        title: 'U Cut Hairstyles: Long, Layered, Medium',
        metaDescription: 'Nine pictures of the U cut, the back of the cut shaped in a curve: long, layered and medium versions, seen from behind in three settings.',
        h1: 'U cut hairstyles: long, layered, medium',
        tagline: 'Nine pictures, three U cuts, each seen from behind in a salon, in the sun and at home.',
        intro: 'A U cut is a back shape cut in a soft curve, like the letter U. These pictures show long straight hair in a soft U, a U cut with layers through the length, and shoulder-blade hair with a slight wave. Each is seen from behind, in a salon, outdoors in late sun and indoors by a tall window. You can type a change, such as caramel balayage, to ask for the cut with it.',
        useCases: [
            'a reference for the back shape',
            'choosing a long, layered or medium U cut',
            'a salon wall print',
            'a desk print',
        ],
        faq: [
            {q: 'Why are the pictures seen from behind?', a: 'A U cut is defined by its back, so the back view shows the curve.'},
            {q: 'What is the difference between the three?', a: 'The long U is straight hair cut in a soft curve. The layered U has layers through the length. The medium U is at the shoulder blades with a slight wave.'},
            {q: 'Can I change the colour?', a: 'Yes. Type the change and the picture is made with it. It is a request, so the result may differ from the sample.'},
        ],
    },
}
